import React, { useState } from 'react';
import BiohackInfoCard from './BiohackInfoCard';
import './BiohackCard.css';
import { Icon } from './Icon.js';

const BiohackCard = ({ hack, isFavourited, isSelected, hasRated = false, onAddToProtocol, onToggleFavourite, onRatingSubmitted }) => {
  const [showInfoCard, setShowInfoCard] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const handleInfoClick = (e) => {
    e.stopPropagation();
    setShowInfoCard(true);
  };

  const handleAddClick = (e) => {
    e.stopPropagation();
    onAddToProtocol(hack);
  };

  const handleFavouriteClick = (e) => {
    e.stopPropagation();
    onToggleFavourite(hack.id);
  };

  // Drag and drop handlers
  const handleDragStart = (e) => {
    if (isSelected) {
      e.preventDefault();
      return;
    }
    
    setIsDragging(true);
    e.dataTransfer.setData('application/json', JSON.stringify(hack));
    e.dataTransfer.effectAllowed = 'copy';
    
    // Create a custom drag image that's the same size as placeholder slots
    const dragImage = e.currentTarget.cloneNode(true);
    dragImage.style.width = '90px';
    dragImage.style.height = '90px';
    dragImage.style.position = 'absolute';
    dragImage.style.top = '-9999px';
    dragImage.style.left = '-9999px';
    dragImage.style.zIndex = '10000';
    dragImage.style.transform = 'none';
    
    // Hide overlay in drag image
    const overlay = dragImage.querySelector('.card-overlay');
    if (overlay) {
      overlay.style.display = 'none';
    }
    
    // Adjust image height in drag image to match placeholder (60px)
    const cardImage = dragImage.querySelector('.card-image');
    if (cardImage) {
      cardImage.style.height = '60px';
      cardImage.style.borderRadius = '10px 10px 0 0';
    }
    
    // Adjust content padding in drag image to match current design
    const cardContent = dragImage.querySelector('.card-content');
    if (cardContent) {
      cardContent.style.padding = '0 12px';
      cardContent.style.height = '30px';
      cardContent.style.display = 'flex';
      cardContent.style.alignItems = 'center';
      cardContent.style.justifyContent = 'center';
    }
    
    // Adjust title font size in drag image to match placeholder
    const cardTitle = dragImage.querySelector('.card-title');
    if (cardTitle) {
      cardTitle.style.fontSize = '0.7rem';
      cardTitle.style.lineHeight = '1.2';
      cardTitle.style.margin = '0';
      cardTitle.style.textAlign = 'center';
    }
    
    document.body.appendChild(dragImage);
    e.dataTransfer.setDragImage(dragImage, 45, 45); // Center the drag image
    
    // Clean up the temporary element after drag starts
    setTimeout(() => {
      if (document.body.contains(dragImage)) {
        document.body.removeChild(dragImage);
      }
    }, 0);
    
    // Add dragging class to body for global styling
    document.body.classList.add('dragging-biohack');
  };

  const handleDragEnd = (e) => {
    setIsDragging(false);
    document.body.classList.remove('dragging-biohack');
  };

  return (
    <>
      <div 
        className={`biohack-card ${isSelected ? 'selected' : ''} ${isDragging ? 'dragging' : ''}`}
        draggable={!isSelected}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        title={isSelected ? 'Already in protocol' : 'Drag to add to protocol'}
      >
        <div className="card-image">
          <img 
            src={hack.image_url || '/default-biohack.jpg'} 
            alt={hack.name}
            onError={(e) => {
              e.target.src = '/default-biohack.jpg';
            }}
          />
          {/* Combined rating and user rating indicator */}
          <div className="card-rating-indicator">
            <Icon name="Star" className={`icon--xs ${hasRated ? 'star-rated' : 'star-not-rated'}`} />
            <span className="rating-text">
              {hack.ease_of_use_score && hack.time_efficiency_score && hack.affordability_score 
                ? ((hack.ease_of_use_score + hack.time_efficiency_score + hack.affordability_score) / 3).toFixed(1)
                : 'N/A'
              }
            </span>
          </div>
          <div className="card-overlay">
            <div className="hover-icons">
              <button 
                className={`icon-btn icon-btn--sm ${isFavourited ? 'icon-btn--liked' : 'icon-btn--secondary'}`}
                onClick={handleFavouriteClick}
                title={isFavourited ? 'Remove from favourites' : 'Add to favourites'}
              >
                <Icon 
                  name="Heart" 
                  className={`icon--sm ${isFavourited ? 'icon--favourited' : 'icon--not-favourited'}`}
                />
              </button>
              
              <button 
                className={`icon-btn icon-btn--sm icon-btn--info-cta ${isSelected ? 'icon-btn--active' : 'icon-btn--secondary'}`}
                onClick={handleAddClick}
                title={isSelected ? 'Already in protocol' : 'Add to protocol'}
                disabled={isSelected}
              >
                <Icon name="Plus" className="icon--sm" />
              </button>
              
              <button 
                className="icon-btn icon-btn--sm icon-btn--secondary"
                onClick={handleInfoClick}
                title="View Details"
              >
                <Icon name="Info" className="icon--sm" />
              </button>
            </div>
          </div>
        </div>
        
        <div className="card-content">
          <h3 className="card-title">{hack.name}</h3>
        </div>
      </div>

      {/* Biohack Info Card Modal */}
      {showInfoCard && (
        <BiohackInfoCard 
          biohack={hack} 
          onClose={() => setShowInfoCard(false)}
          onAddToProtocol={onAddToProtocol}
          isFavourited={isFavourited}
          onToggleFavourite={onToggleFavourite}
          hasRated={hasRated}
          onRatingSubmitted={async (ratings) => {
            // Notify parent component that a rating was submitted
            if (onRatingSubmitted) {
              onRatingSubmitted();
            }
          }}
        />
      )}
    </>
  );
};

export default BiohackCard; 